"use client";
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import { Col, DatePicker, Form, Input, Row, Table, Tooltip } from "antd";

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const glColumns = [
  {
    title: "GL Code",
    dataIndex: "glCode",
    key: "glCode",
    render: (text) => (
      <a
        style={{
          color: "#15CCAE",
          textDecoration: "underline",
          fontWeight: 500,
        }}
      >
        {text}
      </a>
    ),
  },
  {
    title: "Account Name",
    dataIndex: "accountName",
    key: "accountName",
  },
  {
    title: "Debit (N)",
    dataIndex: "debit",
    key: "debit",
    align: "right",
    render: (text) =>
      text ? (
        <span style={{ fontWeight: 600 }}>{text}</span>
      ) : (
        <span style={{ color: "#98A2B3" }}>--</span>
      ),
  },
  {
    title: "Credit (N)",
    dataIndex: "credit",
    key: "credit",
    align: "right",
    render: (text, record) =>
      text ? (
        <span
          style={{
            fontWeight: 600,
            color: record.creditColor || "inherit",
          }}
        >
          {text}
        </span>
      ) : (
        <span style={{ color: "#98A2B3" }}>--</span>
      ),
  },
  {
    title: "Narration",
    dataIndex: "narration",
    key: "narration",
    render: (text) => (
      <span style={{ color: "#667085", fontSize: 13 }}>{text}</span>
    ),
  },
];

const defaultGlData = [
  {
    key: "1",
    glCode: "1020001",
    accountName: "Loan Portfolio -- Consumer",
    debit: "5,000,000",
    credit: null,
    narration: "Loan principal disbursed",
  },
  {
    key: "2",
    glCode: "1101001",
    accountName: "Nostro / settlement",
    debit: null,
    credit: "4,900,000",
    narration: "Net proceeds to customer account",
  },
  {
    key: "3",
    glCode: "2040011",
    accountName: "Unearned income -- fees",
    debit: null,
    credit: "75,000",
    creditColor: "#15CCAE",
    narration: "Processing fee deferred",
  },
  {
    key: "4",
    glCode: "2040012",
    accountName: "Unearned income -- insurance",
    debit: null,
    credit: "25,000",
    creditColor: "#15CCAE",
    narration: "Insurance premium deferred",
  },
];

const Disbursement = (props) => {
  const { onFinish, form, initialValues, loanData } = props;

  const loanPrincipal = loanData?.loanPrincipal ?? "N5,000,000";
  const processingFee = loanData?.processingFee ?? "N75,000";
  const insurance = loanData?.insurance ?? "N25,000";
  const netDisbursement = loanData?.netDisbursement ?? "N4,900,000";
  const creditAccount = loanData?.creditAccount ?? "0012345678 -- Current Account";
  const authorisedBy = loanData?.authorisedBy ?? "O. Adesanya (Branch Manager)";
  const loanAccountRef = loanData?.loanAccountRef ?? "LN-2026-0847-001";
  const glData = loanData?.glData ?? defaultGlData;

  return (
    <div>
      {/* Net Disbursement Card */}
      <div
        style={{
          background: "#f0fdf9",
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          border: "1px solid #a7f3d0",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#344054",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          NET DISBURSEMENT AMOUNT
        </div>
        <div
          style={{
            fontSize: 42,
            fontWeight: 700,
            color: "#15CCAE",
            lineHeight: 1.2,
          }}
        >
          {netDisbursement}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#667085",
            marginBottom: 24,
          }}
        >
          (after fees deducted)
        </div>

        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#98A2B3",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              LOAN PRINCIPAL
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#344054",
              }}
            >
              {loanPrincipal}
            </div>
          </Col>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#98A2B3",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              PROCESSING FEE
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#F87171",
              }}
            >
              -{processingFee}
            </div>
          </Col>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#98A2B3",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              INSURANCE
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#F87171",
              }}
            >
              -{insurance}
            </div>
          </Col>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#98A2B3",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              NET TO ACCOUNT
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#079455",
              }}
            >
              {netDisbursement}
            </div>
          </Col>
        </Row>
      </div>

      {/* GL Posting Table */}
      <div style={{ marginBottom: 8 }}>
        <div style={sectionHeaderStyle}>
          GL POSTING -- DISBURSEMENT ENTRIES
        </div>
        <Table
          columns={glColumns}
          dataSource={glData}
          bordered
          pagination={false}
          size="middle"
          style={{ marginBottom: 24 }}
        />
      </div>

      {/* Disbursement Confirmation */}
      <div style={sectionDividerStyle}>
        <div style={sectionHeaderStyle}>DISBURSEMENT CONFIRMATION</div>

        <Form
          className="custom-form"
          layout="vertical"
          onFinish={onFinish}
          form={form}
          initialValues={{
            creditToAccount: creditAccount,
            authorisedBy: authorisedBy,
            loanAccountReference: loanAccountRef,
            ...initialValues,
          }}
          style={{ width: "100%" }}
        >
          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Credit to Account
                    <Tooltip title="Account to credit the loan amount">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="creditToAccount"
              >
                <Input
                  className="bond-input"
                  style={{ height: 44 }}
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Value Date
                    <Tooltip title="Select the value date for disbursement">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="valueDate"
                rules={[
                  {
                    required: true,
                    message: "Value date is required",
                  },
                ]}
              >
                <DatePicker
                  className="bond-date-picker"
                  style={{
                    width: "100%",
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 11px",
                    fontSize: 14,
                  }}
                  suffixIcon={<CalendarIcon color="#667085" width={20} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Disbursement Authorised By
                    <Tooltip title="Person who authorised the disbursement">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="authorisedBy"
              >
                <Input
                  className="bond-input"
                  style={{ height: 44 }}
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Loan Account Reference
                    <Tooltip title="System-generated loan account reference">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="loanAccountReference"
              >
                <Input
                  className="bond-input"
                  style={{ height: 44, background: "#F9FAFB" }}
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Warning Box */}
      <div
        style={{
          background: "#FFFBEB",
          border: "1px solid #FDE68A",
          borderRadius: 12,
          padding: 16,
          marginTop: 24,
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <WarningOutlined
          style={{
            color: "#D97706",
            fontSize: 20,
            marginTop: 2,
            flexShrink: 0,
          }}
        />
        <div style={{ fontSize: 14, color: "#92400E", lineHeight: 1.6 }}>
          <span style={{ fontWeight: 700 }}>Final confirmation required.</span>{" "}
          This action will disburse {netDisbursement} to the customer's
          account and generate all associated GL entries. This action is{" "}
          <strong>irreversible</strong> and will activate the loan repayment
          schedule.
        </div>
      </div>
    </div>
  );
};

export default Disbursement;
